// src/pages/LandingPage.tsx
import React, { useEffect } from "react";
import pkg from "../../package.json";

// New Components
import HeroPickupSection from "../components/app/HeroPickupSection";
import QuickActionsSection from "../components/app/QuickActionsSection";
import CountrySelectorBar from "../components/app/CountrySelectorBar";
import SEO from "../components/app/SEO";

// Existing Components
import Nav from "../components/app/Nav";
import PromoBanner from "../components/app/PromoBanner";
import FeatureShowcase from "../components/app/FeatureShowcase";
import ServiceUPSSection from "../components/app/ServiceUPSSection";
import PricesSection from "../components/app/PricesSection";
import OurServicesSection from "../components/app/OurServicesSection";
import CoverageSection from "../components/app/CoverageSection";
import CityCoverageMarquee from "../components/app/CityCoverageMarquee";
import ClientsSection from "../components/app/ClientsSection";
import FAQAccordion from "../components/app/FAQAccordion";
import MetricsSection from "../components/app/MetricsSection";
import FloatingButton from "../components/app/FloatingButton";
import Footer from "../components/app/Footer";

import ShippingRouteIMG from "../assets/images/shipping_route.png";

const LandingPage: React.FC = () => {
  useEffect(() => {
    const isProd = process.env.REACT_APP_IS_PROD === "TRUE";
    const envName = isProd ? "PROD" : "TEST";
    console.log(`Versión de la aplicación: v${pkg.version} - ${envName}`);
  }, []);

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://elpaket.com/#organization",
        name: "elPaket",
        url: "https://elpaket.com",
        logo: "https://elpaket.com/logo512.png",
        image: "https://elpaket.com/logo512.png",
        description:
          "Plataforma líder para envíos internacionales desde cualquier estado de USA hacia Venezuela, República Dominicana, Costa Rica, Guatemala y Perú.",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+1-786-838-0122",
          contactType: "Customer Support",
          areaServed: ["US", "VE", "DO", "CR", "GT", "PE"],
          availableLanguage: ["Spanish", "English"],
        },
        sameAs: ["https://instagram.com/elpaket"],
      },
      {
        "@type": "WebSite",
        "@id": "https://elpaket.com/#website",
        url: "https://elpaket.com",
        name: "elPaket",
        description: "Envíos de USA a Latinoamérica con recogida UPS y entrega puerta a puerta.",
        publisher: {
          "@id": "https://elpaket.com/#organization",
        },
        inLanguage: "es",
      },
      {
        "@type": "Service",
        "@id": "https://elpaket.com/#service",
        name: "Envíos de Paquetes de USA a Latinoamérica",
        serviceType: "Logistics & Courier Service",
        provider: {
          "@id": "https://elpaket.com/#organization",
        },
        areaServed: [
          { "@type": "Country", name: "United States" },
          { "@type": "Country", name: "Venezuela" },
          { "@type": "Country", name: "Dominican Republic" },
          { "@type": "Country", name: "Costa Rica" },
          { "@type": "Country", name: "Guatemala" },
          { "@type": "Country", name: "Peru" },
        ],
        description:
          "Servicio integral de envíos puerta a puerta con recogida a domicilio UPS o entrega en tiendas UPS, tracking en tiempo real y cobertura en más de 300 destinos.",
      },
      {
        "@type": "FAQPage",
        "@id": "https://elpaket.com/#faq",
        mainEntity: [
          {
            "@type": "Question",
            name: "¿Por qué elegir elPaket para enviar paquetes a Latinoamérica?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Ofrecemos tarifas competitivas y transparentes, recolección en tu puerta con UPS en los 50 estados de USA, tracking en tiempo real 24/7, seguro incluido y entrega puerta a puerta en más de 300 destinos.",
            },
          },
          {
            "@type": "Question",
            name: "¿A qué países de Latinoamérica puedo enviar con elPaket?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Realizamos envíos desde cualquier estado de USA hacia Venezuela, República Dominicana, Costa Rica, Guatemala y Perú con cobertura en las principales ciudades y regiones.",
            },
          },
          {
            "@type": "Question",
            name: "¿Cuánto tiempo tarda en llegar mi paquete?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Los envíos aéreos toman entre 5 y 15 días hábiles dependiendo del país de destino. Los envíos marítimos tardan entre 3 y 6 semanas. Los tiempos pueden variar según los procesos aduanales.",
            },
          },
          {
            "@type": "Question",
            name: "¿Cómo funciona la recogida (Pick Up) con UPS?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Puedes programar la recolección de tu caja directamente en tu domicilio u oficina en cualquier parte de USA, o llevarla a la tienda UPS (Drop-Off) más cercana utilizando nuestra etiqueta prepagada.",
            },
          },
          {
            "@type": "Question",
            name: "¿Qué artículos están prohibidos enviar?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Está prohibido enviar armas de fuego, municiones, explosivos, drogas, dinero en efectivo, joyas de alto valor, carnes o perecederos no permitidos y material peligroso según normativas internacionales.",
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="Envíos de USA a Latinoamérica | elPaket"
        description="Envía paquetes desde cualquier estado de USA a Venezuela, República Dominicana, Costa Rica, Guatemala y Perú. Recogida UPS, rastreo y entrega puerta a puerta."
        canonicalUrl="https://elpaket.com/"
        schema={schemaMarkup}
      />
      <Nav />
      {/* Country Selector Menu */}
      <CountrySelectorBar />
      {/* New Hero Section with Pickup Form */}
      <HeroPickupSection />

      {/* New SPLIT Section: Tracking + Calculator */}
      <QuickActionsSection />

      {/* Existing Sections */}
      {process.env.REACT_APP_SHOW_DISCOUNT === "TRUE" && <PromoBanner />}
      {/*
      <div
        className="features-ups-wrapper"
        style={{ backgroundImage: `url(${ShippingRouteIMG})` }}
      >
        <div className="features-ups-overlay"></div>
        <FeatureShowcase />
      </div>
      */}
      <ServiceUPSSection />
      {/*
        <OurServicesSection />
        <CityCoverageMarquee />
        <PricesSection />
        <ClientsSection />
      */}
      <FAQAccordion />
      <MetricsSection />
      {/*
      <FloatingButton />
      */}
      <Footer />
    </>
  );
};

export default LandingPage;
